import { IconNavigationChevronDown } from '@s-group/design-system-icons';
import React, { forwardRef, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { CollapsibleGroupContext } from '../CollapsibleGroup/CollapsibleGroupContext.js';
import { useCollapsibleGroup } from '../CollapsibleGroup/hooks.js';
import 'polished';
import '@s-group/design-system-tokens/web/tokens/size.es6.js';
import { useForwardedRef, useSimpleId } from '../common/hooks.js';
import { FormContext } from '../Form/FormContext.js';
import { CollapsibleStyle } from './Collapsible.styles.js';

const defaultProps = {
    alignment: 'default',
    alternative: false,
    border: true,
    rounding: 'sharp',
    sizing: 'medium',
    summaryProps: {},
    toggle: 'after',
};
const LeadingIcon = ({ icon, toggle }) => {
    if (icon && toggle === 'after') {
        const [IconComponent, iconProps] = Array.isArray(icon)
            ? icon
            : 'props' in icon
                ? [icon.type, icon.props]
                : [icon];
        return React.createElement(IconComponent, { className: "leading-icon", ...iconProps });
    }
    return null;
};
const _Collapsible = forwardRef((props, forwardedRef) => {
    const { alignment, // Used only in styling, prevent passing it to DOM
    alternative, // Used only in styling, prevent passing it to DOM
    border, // Used only in styling, prevent passing it to DOM
    children, header, id: propId, icon, open: propOpen, rounding, // Used only in styling, prevent passing it to DOM
    sizing, // Used only in styling, prevent passing it to DOM
    summaryProps, toggle, // Used only in styling, prevent passing it to DOM
    ...rest } = props;
    const ref = useForwardedRef(forwardedRef);
    const { onClick: customOnClickHandler, onKeyDown: customOnKeyDownHandler, onKeyUp: customOnKeyUpHandler, ...summaryRest } = summaryProps !== null && summaryProps !== void 0 ? summaryProps : defaultProps.summaryProps;
    const [open, setOpen] = React.useState(propOpen !== null && propOpen !== void 0 ? propOpen : false);
    const groupContext = useCollapsibleGroup(ref);
    const { accordion, dispatch, open: currentlyOpen } = groupContext !== null && groupContext !== void 0 ? groupContext : {};
    const summaryRef = React.useRef(null);
    const id = propId !== null && propId !== void 0 ? propId : useSimpleId();
    const handleClick = (event) => {
        // To avoid race condition between React and the browser default
        // behavior in toggling `open` property state, disable the browser
        // default behavior and let React manage the state.
        // https://github.com/facebook/react/issues/15486#issuecomment-873516817
        event.preventDefault();
        if (customOnClickHandler) {
            customOnClickHandler(event);
        }
        if (accordion && dispatch) {
            dispatch({ type: open ? 'close' : 'open', ref });
        }
        else {
            setOpen(state => !state);
        }
    };
    // Pressing the Enter key or Spacebar on the <summary> element triggers
    // a click event by default in the browser. However, for now user-event
    // library used in test to simulate keyboard events doesn't replicate
    // this behavior. To avoid the need to polyfill this behavior for test
    // cases in all projects using the component, the click event trigger
    // behavior is explicitly implemented here.
    //
    // Note: Enter key triggers the event on keyDown event and Spacebar on
    // keyUp event.
    //
    // Related user-event library logic:
    // https://github.com/testing-library/user-event/blob/main/src/event/behavior/keypress.ts
    const handleKeyDown = (event) => {
        if (customOnKeyDownHandler) {
            customOnKeyDownHandler(event);
        }
        if (event.key === 'Enter' && summaryRef.current) {
            event.preventDefault();
            summaryRef.current.click();
        }
    };
    const handleKeyUp = (event) => {
        if (customOnKeyUpHandler) {
            customOnKeyUpHandler(event);
        }
        if (event.key === ' ' && summaryRef.current) {
            event.preventDefault();
            summaryRef.current.click();
        }
    };
    useEffect(() => {
        if (accordion &&
            currentlyOpen !== undefined &&
            (ref === null || ref === void 0 ? void 0 : ref.current) !== null) {
            // In accordion mode the parent CollapsibleGroupContext
            // controls, which Collapsible is open at a time.
            setOpen((currentlyOpen === null || currentlyOpen === void 0 ? void 0 : currentlyOpen.current) === (ref === null || ref === void 0 ? void 0 : ref.current));
        }
        else {
            // In a standalone controlled component, update the internal
            // state if the `open` property is changed externally.
            setOpen(state => propOpen !== null && propOpen !== void 0 ? propOpen : state);
        }
    }, [accordion, currentlyOpen, propOpen, ref]);
    return (React.createElement("details", { id: id, open: open, ...rest, ref: ref },
        React.createElement("summary", { onClick: handleClick, onKeyDown: handleKeyDown, onKeyUp: handleKeyUp, tabIndex: 0, ...summaryRest, ref: summaryRef },
            React.createElement(LeadingIcon, { icon: icon, toggle: toggle }),
            React.createElement("div", { className: "header-wrapper" }, header),
            React.createElement(IconNavigationChevronDown, { className: "toggle-icon" })),
        React.createElement(CollapsibleGroupContext.Provider, { value: undefined }, children)));
});
_Collapsible.displayName = '_Collapsible';
const Collapsible = styled(_Collapsible).attrs(({ alignment, alternative, border, icon, rounding, sizing, toggle, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const collapsibleGroupContext = useContext(CollapsibleGroupContext);
    const formContext = useContext(FormContext);
    return {
        alignment: (_a = alignment !== null && alignment !== void 0 ? alignment : collapsibleGroupContext === null || collapsibleGroupContext === void 0 ? void 0 : collapsibleGroupContext.alignment) !== null && _a !== void 0 ? _a : defaultProps.alignment,
        alternative: (_c = (_b = alternative !== null && alternative !== void 0 ? alternative : collapsibleGroupContext === null || collapsibleGroupContext === void 0 ? void 0 : collapsibleGroupContext.alternative) !== null && _b !== void 0 ? _b : formContext === null || formContext === void 0 ? void 0 : formContext.alternative) !== null && _c !== void 0 ? _c : defaultProps.alternative,
        border: (_d = border !== null && border !== void 0 ? border : collapsibleGroupContext === null || collapsibleGroupContext === void 0 ? void 0 : collapsibleGroupContext.border) !== null && _d !== void 0 ? _d : defaultProps.border,
        icon: icon !== null && icon !== void 0 ? icon : collapsibleGroupContext === null || collapsibleGroupContext === void 0 ? void 0 : collapsibleGroupContext.icon,
        rounding: (_e = rounding !== null && rounding !== void 0 ? rounding : collapsibleGroupContext === null || collapsibleGroupContext === void 0 ? void 0 : collapsibleGroupContext.rounding) !== null && _e !== void 0 ? _e : defaultProps.rounding,
        sizing: (_g = (_f = sizing !== null && sizing !== void 0 ? sizing : collapsibleGroupContext === null || collapsibleGroupContext === void 0 ? void 0 : collapsibleGroupContext.sizing) !== null && _f !== void 0 ? _f : formContext === null || formContext === void 0 ? void 0 : formContext.sizing) !== null && _g !== void 0 ? _g : defaultProps.sizing,
        toggle: (_h = toggle !== null && toggle !== void 0 ? toggle : collapsibleGroupContext === null || collapsibleGroupContext === void 0 ? void 0 : collapsibleGroupContext.toggle) !== null && _h !== void 0 ? _h : defaultProps.toggle,
    };
}) `
    ${CollapsibleStyle}
`;

export { Collapsible, defaultProps };
//# sourceMappingURL=Collapsible.js.map
